import { CallbackGroup } from './CallbackGroup';

export class EventSystem {

    constructor() {
        /**
         * Underscore prefix used to suggested the variable is private.
         * @type {Object<string, CallbackGroup>}
         */
        this._events = {};
    }

    /**
     * Triggers the execution of all callbacks stored in an event specified
     * by key. If the event attempted doesn't exist, nothing will happen.
     * 
     * @param {string} eventKey - The event to trigger.
     * @param {...any} args - Any arguments and data to send to the event's callbacks as parameters.
     * @returns {boolean} Whether or not any callbacks were successfully executed.
     */
    async invoke( eventKey, ...args ) {
        if ( this.hasEvent( eventKey ) ) {
            const callbackGroup = this._events[ eventKey ];
            return await callbackGroup.invoke( ...args );
        }
        return false;
    }

    /**
     * Adds a callback to an event key. Callbacks are assigned ids when added
     * to the event system. This callback id is returned by this function. If
     * the callback being added already exists in the event, then it won't be
     * added again and the id of the existing callback will be returned. If a
     * callback is unable to be added for some reason, `undefined` will be
     * returned.
     * 
     * @param {string} eventKey - The key of the event to store the callback in.
     * @param {function} callback - The callback to add to the event.
     * @returns {string|undefined} The id of the added callback or the callback that already existed.
     */
    addEventListener( eventKey, callback ) {
        if ( !this.hasEvent( eventKey ) ) {
            this._events[ eventKey ] = new CallbackGroup();
        }

        const callbackGroup = this._events[ eventKey ];
        return callbackGroup.add( callback );
    }

    /**
     * Removes a callback from an event key. If the callback is successfully
     * removed, the id of the callback is returned. If the callback doesn't
     * exist, `undefined` will be returned. 
     * 
     * @param {string} eventKey - The key of the event to remove the callback from.
     * @param {function|string} callback - The callback to remove from the event.
     * @returns {string|undefined} The id of the removed callback.
     */
    removeEventListener( eventKey, callback ) {
        if ( this.hasEvent( eventKey ) ) {
            const callbackGroup = this._events[ eventKey ];
            return callbackGroup.remove( callback );
        }
        return undefined;
    }

    /**
     * Returns the number of callbacks stored in an event. If the event doesn't
     * exist, `0` will be returned.
     * 
     * @param {string} eventKey - The key of the events to get the callback count from.
     * @returns {number} The number of callbacks stored on an event.
     */
    getCallbackCount( eventKey ) {
        if ( this.hasEvent( eventKey ) ) {
            const callbackGroup = this._events[ eventKey ];
            return callbackGroup.getCount();
        }
        return 0;
    }

    /**
     * Returns the number of events stored in the event system.
     * 
     * @returns {number} The number of events stored in the event system.
     */
    getEventCount() {
        return Object.keys( this._events ).length;
    }

    /**
     * Returns whether or not an event key exists.
     * 
     * @param {string} eventKey - The key of the event to check.
     * @returns {boolean} Whether or not the event key existed in the event system.
     */
    hasEvent( eventKey ) {
        return Boolean( this._events[ eventKey ] );
    }

    /**
     * Removes all stored events and callbacks.
     */
    reset() {
        this._events = {};
    }

}
